import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Settings,
  Palette,
  Clock,
  CreditCard,
  Building,
  Save,
  RotateCcw,
  Users,
  Upload,
  X,
  Image as ImageIcon,
  Paintbrush,
  Plus,
  Trash2,
  Edit2,
  CalendarOff,
  Palmtree,
  MessageCircle,
  Mail,
  Phone,
  CheckCircle2,
  AlertCircle,
  FileText,
  Download,
  Eye,
  AlertTriangle,
  RefreshCcw,
  Lock,
  UserPlus,
  Shield,
  Link2,
  QrCode,
  Smartphone,
  Bell,
  Send,
  Target,
  TrendingUp,
  Package,
  Calendar,
  BarChart3,
  Zap
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import GestioneOperatori from "@/components/GestioneOperatori";

const GIORNI = [
  { key: "lunedi", label: "Lunedì" },
  { key: "martedi", label: "Martedì" },
  { key: "mercoledi", label: "Mercoledì" },
  { key: "giovedi", label: "Giovedì" },
  { key: "venerdi", label: "Venerdì" },
  { key: "sabato", label: "Sabato" },
  { key: "domenica", label: "Domenica" },
];

const METODI_PAGAMENTO_OPTIONS = [
  { value: "contanti", label: "Contanti" },
  { value: "carta", label: "Carta/POS" },
  { value: "giftcard", label: "Gift Card" },
  { value: "bonifico", label: "Bonifico" },
  { value: "satispay", label: "Satispay" },
];

export default function Impostazioni() {
  const { token } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingListino, setUploadingListino] = useState(false);
  const fileInputRef = useRef(null);
  const listinoInputRef = useRef(null);
  
  // Reset database
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetting, setResetting] = useState(false);

  // Cambio Password
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Gestione Utenti
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUserData, setNewUserData] = useState({
    email: "",
    password: "",
    nome: "",
    ruolo: "operatore"
  });

  // Integrazioni WhatsApp
  const [whatsappConfig, setWhatsappConfig] = useState({
    connected: false,
    phoneNumber: "",
    destinatari: [],
    notifiche: {
      promemoria_appuntamenti: true,
      conferma_prenotazione: true,
      report_giornaliero: false,
      report_settimanale: false,
      nuovi_clienti: false,
      cancellazioni: true
    }
  });
  const [newDestinatario, setNewDestinatario] = useState({ nome: "", telefono: "" });
  const [savingWhatsapp, setSavingWhatsapp] = useState(false);

  // QR Code WhatsApp
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [qrStatus, setQrStatus] = useState('waiting'); // 'waiting', 'scanning', 'connected'

  // Configurazione Wingman Report
  const [wingmanConfig, setWingmanConfig] = useState({
    attivo: false,
    destinatari_team: [],
    obiettivo_incasso_giornaliero: 500,
    obiettivo_appuntamenti_giornalieri: 10,
    obiettivo_clienti_nuovi_settimana: 5,
    report_mattina_attivo: true,
    report_mattina_ora: "08:00",
    report_sera_attivo: true,
    report_sera_ora: "20:00",
    alert_scorte_attivo: true,
    soglia_scorte_minima: 5,
    report_mensile_attivo: true,
    report_mensile_giorno: 1
  });
  const [newTeamMember, setNewTeamMember] = useState({ nome: "", telefono: "", ruolo: "operatore" });
  const [savingWingman, setSavingWingman] = useState(false);
  const [reportPreview, setReportPreview] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetchUsers();
    fetchWhatsappConfig();
    fetchWingmanConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/configurazione`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      toast.error("Errore nel caricamento configurazione");
    } finally {
      setLoading(false);
    }
  };

  // Fetch utenti
  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error("Errore caricamento utenti:", error);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Fetch configurazione WhatsApp
  const fetchWhatsappConfig = async () => {
    try {
      const response = await axios.get(`${API}/configurazione/whatsapp`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setWhatsappConfig(response.data);
      }
    } catch (error) {
      // Config non esistente, usa default
      console.log("WhatsApp config non trovata, uso default");
    }
  };

  // Salva configurazione WhatsApp
  const saveWhatsappConfig = async () => {
    setSavingWhatsapp(true);
    try {
      await axios.post(`${API}/configurazione/whatsapp`, whatsappConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configurazione WhatsApp salvata!");
    } catch (error) {
      toast.error("Errore nel salvataggio configurazione WhatsApp");
    } finally {
      setSavingWhatsapp(false);
    }
  };

  // Fetch configurazione Wingman
  const fetchWingmanConfig = async () => {
    try {
      const response = await axios.get(`${API}/configurazione/wingman`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setWingmanConfig(response.data);
      }
    } catch (error) {
      console.log("Wingman config non trovata, uso default");
    }
  };

  // Salva configurazione Wingman
  const saveWingmanConfig = async () => {
    setSavingWingman(true);
    try {
      await axios.post(`${API}/configurazione/wingman`, wingmanConfig, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configurazione Report Team salvata!");
    } catch (error) {
      toast.error("Errore nel salvataggio configurazione");
    } finally {
      setSavingWingman(false);
    }
  };

  // Aggiungi membro team
  const addTeamMember = () => {
    if (!newTeamMember.nome || !newTeamMember.telefono) {
      toast.error("Inserisci nome e telefono");
      return;
    }
    setWingmanConfig(prev => ({
      ...prev,
      destinatari_team: [
        ...prev.destinatari_team,
        { ...newTeamMember, id: Date.now().toString() }
      ]
    }));
    setNewTeamMember({ nome: "", telefono: "", ruolo: "operatore" });
    toast.success("Membro aggiunto!");
  };

  // Rimuovi membro team
  const removeTeamMember = (id) => {
    setWingmanConfig(prev => ({
      ...prev,
      destinatari_team: prev.destinatari_team.filter(m => m.id !== id)
    }));
  };

  // Carica anteprima report
  const loadReportPreview = async (tipo) => {
    setLoadingReport(true);
    try {
      const response = await axios.get(`${API}/report/${tipo}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportPreview({ tipo, data: response.data });
    } catch (error) {
      toast.error("Errore nel caricamento report");
    } finally {
      setLoadingReport(false);
    }
  };

  // Aggiungi destinatario WhatsApp
  const addDestinatario = () => {
    if (!newDestinatario.nome || !newDestinatario.telefono) {
      toast.error("Inserisci nome e telefono");
      return;
    }
    // Formatta numero (rimuovi spazi, aggiungi +39 se non presente)
    let tel = newDestinatario.telefono.replace(/\s/g, "");
    if (!tel.startsWith("+")) {
      tel = "+39" + tel;
    }
    setWhatsappConfig(prev => ({
      ...prev,
      destinatari: [...prev.destinatari, { ...newDestinatario, telefono: tel, id: Date.now() }]
    }));
    setNewDestinatario({ nome: "", telefono: "" });
    toast.success("Destinatario aggiunto");
  };

  // Rimuovi destinatario
  const removeDestinatario = (id) => {
    setWhatsappConfig(prev => ({
      ...prev,
      destinatari: prev.destinatari.filter(d => d.id !== id)
    }));
  };

  // Toggle notifica WhatsApp
  const toggleNotifica = (key) => {
    setWhatsappConfig(prev => ({
      ...prev,
      notifiche: {
        ...prev.notifiche,
        [key]: !prev.notifiche[key]
      }
    }));
  };

  // Cambio Password
  const handleChangePassword = async () => {
    if (!passwordData.current_password || !passwordData.new_password) {
      toast.error("Compila tutti i campi");
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("Le password non coincidono");
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }

    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Password cambiata con successo!");
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel cambio password");
    } finally {
      setChangingPassword(false);
    }
  };

  // Crea nuovo utente
  const handleCreateUser = async () => {
    if (!newUserData.email || !newUserData.password || !newUserData.nome) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    try {
      await axios.post(`${API}/users`, newUserData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Utente creato con successo!");
      setUserDialogOpen(false);
      setNewUserData({ email: "", password: "", nome: "", ruolo: "operatore" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione utente");
    }
  };

  // Elimina utente
  const handleDeleteUser = async (userId) => {
    if (!confirm("Sei sicuro di voler eliminare questo utente?")) return;

    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Utente eliminato");
      fetchUsers();
    } catch (error) {
      toast.error("Errore nell'eliminazione utente");
    }
  };

  // Toggle attivo/disattivo utente
  const handleToggleUser = async (user) => {
    try {
      await axios.put(`${API}/users/${user.id}`, {
        attivo: !user.attivo
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(user.attivo ? "Utente disattivato" : "Utente attivato");
      fetchUsers();
    } catch (error) {
      toast.error("Errore nell'aggiornamento utente");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/configurazione`, config, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configurazione salvata!");
    } catch (error) {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (path, value) => {
    setConfig(prev => {
      const newConfig = { ...prev };
      const keys = path.split('.');
      let current = newConfig;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newConfig;
    });
  };

  const updateOrario = (giorno, field, value) => {
    setConfig(prev => ({
      ...prev,
      orari: {
        ...prev.orari,
        [giorno]: {
          ...prev.orari[giorno],
          [field]: value
        }
      }
    }));
  };

  const toggleMetodoPagamento = (metodo) => {
    setConfig(prev => {
      const metodi = prev.metodi_pagamento || [];
      if (metodi.includes(metodo)) {
        return { ...prev, metodi_pagamento: metodi.filter(m => m !== metodo) };
      } else {
        return { ...prev, metodi_pagamento: [...metodi, metodo] };
      }
    });
  };

  // Gestione chiusure straordinarie del salone
  const addChiusuraSalone = () => {
    setConfig(prev => ({
      ...prev,
      chiusure_straordinarie: [...(prev.chiusure_straordinarie || []), {
        id: Date.now().toString(),
        data: "",
        data_fine: "",
        motivo: "",
        tipo: "singolo"
      }]
    }));
  };

  const updateChiusuraSalone = (index, field, value) => {
    setConfig(prev => ({
      ...prev,
      chiusure_straordinarie: (prev.chiusure_straordinarie || []).map((c, i) => 
        i === index ? { ...c, [field]: value } : c
      )
    }));
  };

  const removeChiusuraSalone = (index) => {
    setConfig(prev => ({
      ...prev,
      chiusure_straordinarie: (prev.chiusure_straordinarie || []).filter((_, i) => i !== index)
    }));
  };

  // Upload logo file
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Verifica tipo file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Formato non supportato. Usa: JPEG, PNG, GIF, WEBP o PDF");
      return;
    }

    // Verifica dimensione (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File troppo grande. Massimo 5MB");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload/logo`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success) {
        // Usa l'URL relativo - il backend serve i file statici
        const logoUrl = response.data.url;
        updateConfig('logo_url', logoUrl);
        toast.success("Logo caricato con successo!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel caricamento del logo");
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Elimina logo
  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API}/upload/logo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      updateConfig('logo_url', null);
      toast.success("Logo rimosso");
    } catch (error) {
      toast.error("Errore nella rimozione del logo");
    }
  };

  // Upload listino prezzi
  const handleListinoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Verifica tipo file (solo PDF)
    if (file.type !== 'application/pdf') {
      toast.error("Il listino deve essere un file PDF");
      return;
    }
    
    // Verifica dimensione (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Il file non può superare i 10MB");
      return;
    }
    
    setUploadingListino(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload/listino`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        const listinoUrl = `${API.replace('/api', '')}${response.data.url}`;
        updateConfig('listino_url', listinoUrl);
        updateConfig('listino_filename', response.data.filename);
        toast.success("Listino caricato con successo!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nel caricamento del listino");
    } finally {
      setUploadingListino(false);
      if (listinoInputRef.current) {
        listinoInputRef.current.value = '';
      }
    }
  };

  // Elimina listino
  const handleDeleteListino = async () => {
    try {
      await axios.delete(`${API}/upload/listino`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      updateConfig('listino_url', null);
      updateConfig('listino_filename', null);
      toast.success("Listino rimosso");
    } catch (error) {
      toast.error("Errore nella rimozione del listino");
    }
  };

  // Reset Database per Deploy
  const handleResetDatabase = async () => {
    if (resetConfirm !== "RESET") {
      toast.error("Digita 'RESET' per confermare");
      return;
    }
    
    setResetting(true);
    try {
      const response = await axios.post(`${API}/reset-database?conferma=RESET_CONFERMATO`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setResetDialogOpen(false);
        setResetConfirm("");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il reset");
    } finally {
      setResetting(false);
    }
  };

  const resetToDefault = async () => {
    if (!window.confirm("Sei sicuro di voler ripristinare le impostazioni predefinite?")) return;
    
    const defaultConfig = {
      nome_salone: "I CIRILLO PARRUCCHIERI",
      logo_url: null,
      colore_primario: "#2563eb",
      colore_secondario: "#0f172a",
      colore_accento: "#10b981",
      colore_donna: "#ec4899",
      colore_uomo: "#3b82f6",
      colore_unisex: "#8b5cf6",
      durata_slot: 30,
      anticipo_minimo: 60,
      cancellazione_limite: 24,
      metodi_pagamento: ["contanti", "carta", "giftcard"],
      orari: {
        lunedi: { attivo: true, apertura: "09:00", chiusura: "19:00", pausa_inizio: "13:00", pausa_fine: "14:00" },
        martedi: { attivo: true, apertura: "09:00", chiusura: "19:00", pausa_inizio: "13:00", pausa_fine: "14:00" },
        mercoledi: { attivo: true, apertura: "09:00", chiusura: "19:00", pausa_inizio: "13:00", pausa_fine: "14:00" },
        giovedi: { attivo: true, apertura: "09:00", chiusura: "19:00", pausa_inizio: "13:00", pausa_fine: "14:00" },
        venerdi: { attivo: true, apertura: "09:00", chiusura: "19:00", pausa_inizio: "13:00", pausa_fine: "14:00" },
        sabato: { attivo: true, apertura: "09:00", chiusura: "18:00", pausa_inizio: null, pausa_fine: null },
        domenica: { attivo: false, apertura: "09:00", chiusura: "13:00", pausa_inizio: null, pausa_fine: null }
      }
    };
    
    setConfig(prev => ({ ...prev, ...defaultConfig }));
    toast.success("Impostazioni ripristinate");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="impostazioni">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Playfair Display, serif' }}>
            Impostazioni
          </h2>
          <p className="text-slate-500">Personalizza il tuo salone</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetToDefault}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Ripristina
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Salvataggio..." : "Salva Modifiche"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="generale" className="space-y-6">
        <TabsList className="flex flex-wrap gap-1 h-auto p-1">
          <TabsTrigger value="generale" className="gap-2 text-xs sm:text-sm">
            <Building className="w-4 h-4" />
            <span className="hidden sm:inline">Generale</span>
          </TabsTrigger>
          <TabsTrigger value="operatori" className="gap-2 text-xs sm:text-sm">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Operatori</span>
          </TabsTrigger>
          <TabsTrigger value="orari" className="gap-2 text-xs sm:text-sm">
            <Clock className="w-4 h-4" />
            <span className="hidden sm:inline">Orari</span>
          </TabsTrigger>
          <TabsTrigger value="colorbar" className="gap-2 text-xs sm:text-sm">
            <Palette className="w-4 h-4" />
            <span className="hidden sm:inline">Color Bar</span>
          </TabsTrigger>
          <TabsTrigger value="colori" className="gap-2 text-xs sm:text-sm">
            <Paintbrush className="w-4 h-4" />
            <span className="hidden sm:inline">Colori</span>
          </TabsTrigger>
          <TabsTrigger value="pagamenti" className="gap-2 text-xs sm:text-sm">
            <CreditCard className="w-4 h-4" />
            <span className="hidden sm:inline">Pagamenti</span>
          </TabsTrigger>
          <TabsTrigger value="notifiche" className="gap-2 text-xs sm:text-sm">
            <MessageCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Notifiche</span>
          </TabsTrigger>
          <TabsTrigger value="marketing" className="gap-2 text-xs sm:text-sm">
            <Users className="w-4 h-4" />
            <span className="hidden sm:inline">Marketing</span>
          </TabsTrigger>
          <TabsTrigger value="magazzino" className="gap-2 text-xs sm:text-sm" data-testid="tab-magazzino-settings">
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline">Magazzino</span>
          </TabsTrigger>
          <TabsTrigger value="sicurezza" className="gap-2 text-xs sm:text-sm">
            <Shield className="w-4 h-4" />
            <span className="hidden sm:inline">Sicurezza</span>
          </TabsTrigger>
          <TabsTrigger value="utenti" className="gap-2 text-xs sm:text-sm">
            <UserPlus className="w-4 h-4" />
            <span className="hidden sm:inline">Utenti</span>
          </TabsTrigger>
          <TabsTrigger value="integrazioni" className="gap-2 text-xs sm:text-sm">
            <Link2 className="w-4 h-4" />
            <span className="hidden sm:inline">Integrazioni</span>
          </TabsTrigger>
        </TabsList>

        {/* OPERATORI */}
        <TabsContent value="operatori">
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <GestioneOperatori />
            </CardContent>
          </Card>
        </TabsContent>

        {/* GENERALE */}
        <TabsContent value="generale">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building className="w-5 h-5 text-blue-600" />
                Informazioni Salone
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Section */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <Label className="text-base font-semibold">Logo del Salone</Label>
                <div className="flex items-center gap-6">
                  {/* Logo Preview */}
                  <div className="relative">
                    {config?.logo_url ? (
                      <div className="relative">
                        <img 
                          src={config.logo_url} 
                          alt="Logo salone"
                          className="w-24 h-24 rounded-xl object-cover border-2 border-slate-200"
                        />
                        <button
                          onClick={handleDeleteLogo}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-xl bg-slate-200 flex items-center justify-center border-2 border-dashed border-slate-300">
                        <ImageIcon className="w-8 h-8 text-slate-400" />
                      </div>
                    )}
                  </div>
                  
                  {/* Upload Options */}
                  <div className="flex-1 space-y-4">
                    {/* File Upload Button */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Carica Logo</Label>
                      <div className="flex gap-2">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                          onChange={handleLogoUpload}
                          className="hidden"
                          id="logo-upload"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploading}
                          className="flex items-center gap-2"
                        >
                          {uploading ? (
                            <>
                              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                              Caricamento...
                            </>
                          ) : (
                            <>
                              <Upload className="w-4 h-4" />
                              Scegli File
                            </>
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">
                        Formati supportati: JPEG, PNG, GIF, WEBP, PDF (max 5MB)
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Listino Prezzi Section */}
              <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg space-y-4 border border-emerald-200">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5 text-emerald-600" />
                  Listino Prezzi (PDF)
                </Label>
                <div className="flex items-center gap-6">
                  {/* Listino Preview */}
                  <div className="relative">
                    {config?.listino_url ? (
                      <div className="relative">
                        <div className="w-24 h-24 rounded-xl bg-emerald-100 flex items-center justify-center border-2 border-emerald-300">
                          <FileText className="w-10 h-10 text-emerald-600" />
                        </div>
                        <button
                          onClick={handleDeleteListino}
                          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                          title="Rimuovi listino"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-xl bg-slate-100 flex items-center justify-center border-2 border-dashed border-slate-300">
                        <FileText className="w-8 h-8 text-slate-400" />
                      </div>
                    )}
                  </div>
                  
                  {/* Listino Actions */}
                  <div className="flex-1 space-y-4">
                    {config?.listino_url ? (
                      <div className="space-y-3">
                        <p className="text-sm text-emerald-700 font-medium">
                          ✓ Listino caricato
                        </p>
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(config.listino_url, '_blank')}
                            className="flex items-center gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          >
                            <Eye className="w-4 h-4" />
                            Visualizza
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            asChild
                            className="flex items-center gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          >
                            <a href={config.listino_url} download>
                              <Download className="w-4 h-4" />
                              Scarica
                            </a>
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Carica Listino PDF</Label>
                        <div className="flex gap-2">
                          <input
                            ref={listinoInputRef}
                            type="file"
                            accept="application/pdf"
                            onChange={handleListinoUpload}
                            className="hidden"
                            id="listino-upload"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => listinoInputRef.current?.click()}
                            disabled={uploadingListino}
                            className="flex items-center gap-2 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                          >
                            {uploadingListino ? (
                              <>
                                <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
                                Caricamento...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4" />
                                Carica PDF
                              </>
                            )}
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500">
                          Carica il tuo listino prezzi in formato PDF (max 10MB)
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Nome Salone</Label>
                  <Input
                    value={config?.nome_salone || ""}
                    onChange={(e) => updateConfig('nome_salone', e.target.value)}
                    placeholder="Il nome del tuo salone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Telefono</Label>
                  <Input
                    value={config?.telefono || ""}
                    onChange={(e) => updateConfig('telefono', e.target.value)}
                    placeholder="+39 333 1234567"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={config?.email || ""}
                    onChange={(e) => updateConfig('email', e.target.value)}
                    placeholder="info@salone.it"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Sito Web</Label>
                  <Input
                    value={config?.sito_web || ""}
                    onChange={(e) => updateConfig('sito_web', e.target.value)}
                    placeholder="www.iltuosalone.it"
                  />
                </div>
              </div>

              {/* Indirizzo completo */}
              <div className="border-t pt-6">
                <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Building className="w-4 h-4 text-slate-600" />
                  Indirizzo
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label>Via / Indirizzo</Label>
                    <Input
                      value={config?.indirizzo || ""}
                      onChange={(e) => updateConfig('indirizzo', e.target.value)}
                      placeholder="Via Roma 1"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>CAP</Label>
                    <Input
                      value={config?.cap || ""}
                      onChange={(e) => updateConfig('cap', e.target.value)}
                      placeholder="00100"
                      maxLength={5}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Città</Label>
                    <Input
                      value={config?.citta || ""}
                      onChange={(e) => updateConfig('citta', e.target.value)}
                      placeholder="Roma"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Provincia</Label>
                    <Input
                      value={config?.provincia || ""}
                      onChange={(e) => updateConfig('provincia', e.target.value)}
                      placeholder="RM"
                      maxLength={2}
                      className="uppercase"
                    />
                  </div>
                </div>
              </div>

              {/* Dati Fiscali */}
              <div className="border-t pt-6">
                <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-slate-600" />
                  Dati Fiscali
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Partita IVA</Label>
                    <Input
                      value={config?.partita_iva || ""}
                      onChange={(e) => updateConfig('partita_iva', e.target.value)}
                      placeholder="IT12345678901"
                      maxLength={13}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Codice Fiscale</Label>
                    <Input
                      value={config?.codice_fiscale || ""}
                      onChange={(e) => updateConfig('codice_fiscale', e.target.value.toUpperCase())}
                      placeholder="RSSMRA80A01H501Z"
                      maxLength={16}
                      className="uppercase"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Ragione Sociale</Label>
                    <Input
                      value={config?.ragione_sociale || ""}
                      onChange={(e) => updateConfig('ragione_sociale', e.target.value)}
                      placeholder="Nome Srl o Ditta Individuale"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Codice SDI</Label>
                    <Input
                      value={config?.codice_sdi || ""}
                      onChange={(e) => updateConfig('codice_sdi', e.target.value.toUpperCase())}
                      placeholder="XXXXXXX"
                      maxLength={7}
                      className="uppercase"
                    />
                    <p className="text-xs text-slate-400">Per fatturazione elettronica</p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="border-t pt-6">
                <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-slate-600" />
                  Social Media
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <span className="w-5 h-5 bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold">IG</span>
                      </span>
                      Instagram
                    </Label>
                    <Input
                      value={config?.instagram || ""}
                      onChange={(e) => updateConfig('instagram', e.target.value.replace('@', ''))}
                      placeholder="iltuosalone"
                    />
                    <p className="text-xs text-slate-400">Solo username, senza @</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <span className="w-5 h-5 bg-blue-600 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold">f</span>
                      </span>
                      Facebook
                    </Label>
                    <Input
                      value={config?.facebook || ""}
                      onChange={(e) => updateConfig('facebook', e.target.value)}
                      placeholder="iltuosalone"
                    />
                    <p className="text-xs text-slate-400">Nome pagina o username</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <span className="w-5 h-5 bg-green-500 rounded flex items-center justify-center">
                        <Phone className="w-3 h-3 text-white" />
                      </span>
                      WhatsApp Business
                    </Label>
                    <Input
                      value={config?.whatsapp || ""}
                      onChange={(e) => updateConfig('whatsapp', e.target.value)}
                      placeholder="+39 333 1234567"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <span className="w-5 h-5 bg-red-500 rounded flex items-center justify-center">
                        <span className="text-white text-xs font-bold">G</span>
                      </span>
                      Google Business
                    </Label>
                    <Input
                      value={config?.google_business || ""}
                      onChange={(e) => updateConfig('google_business', e.target.value)}
                      placeholder="Link pagina Google"
                    />
                  </div>
                </div>
              </div>

              {/* Impostazioni Color Bar */}
              <div className="border-t pt-6">
                <h4 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <Palette className="w-4 h-4 text-slate-600" />
                  Impostazioni Color Bar
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Grammatura Base Default</Label>
                    <Select
                      value={config?.colorbar_grammatura_base || "45g"}
                      onValueChange={(v) => updateConfig('colorbar_grammatura_base', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["30g", "35g", "40g", "45g", "50g", "55g", "60g"].map(g => (
                          <SelectItem key={g} value={g}>{g}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400">Per ritocco radici</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Grammatura Lunghezze Default</Label>
                    <Select
                      value={config?.colorbar_grammatura_lunghezze || "30g"}
                      onValueChange={(v) => updateConfig('colorbar_grammatura_lunghezze', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["20g", "25g", "30g", "35g", "40g", "45g"].map(g => (
                          <SelectItem key={g} value={g}>{g}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400">Per tonalizzante</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Ossigeno Default Base</Label>
                    <Select
                      value={config?.colorbar_ossigeno_base || "20 vol"}
                      onValueChange={(v) => updateConfig('colorbar_ossigeno_base', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["6 vol", "10 vol", "20 vol", "30 vol", "40 vol"].map(o => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Ossigeno Default Lunghezze</Label>
                    <Select
                      value={config?.colorbar_ossigeno_lunghezze || "10 vol"}
                      onValueChange={(v) => updateConfig('colorbar_ossigeno_lunghezze', v)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["6 vol", "10 vol", "20 vol", "30 vol", "40 vol"].map(o => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Tempo Posa Default (min)</Label>
                    <Input
                      type="number"
                      min="5"
                      step="5"
                      value={config?.colorbar_tempo_posa || 35}
                      onChange={(e) => updateConfig('colorbar_tempo_posa', parseInt(e.target.value))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Colori Miscela</Label>
                    <Select
                      value={String(config?.colorbar_max_colori || 4)}
                      onValueChange={(v) => updateConfig('colorbar_max_colori', parseInt(v))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2">2 colori</SelectItem>
                        <SelectItem value="3">3 colori</SelectItem>
                        <SelectItem value="4">4 colori</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400">Colori miscelabili</p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6">
                <h4 className="font-semibold text-slate-800 mb-4">Impostazioni Appuntamenti</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label>Durata Slot (minuti)</Label>
                    <Input
                      type="number"
                      min="15"
                      step="15"
                      value={config?.durata_slot || 30}
                      onChange={(e) => updateConfig('durata_slot', parseInt(e.target.value))}
                    />
                    <p className="text-xs text-slate-400">Intervallo tra gli appuntamenti</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Anticipo Minimo (minuti)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="30"
                      value={config?.anticipo_minimo || 60}
                      onChange={(e) => updateConfig('anticipo_minimo', parseInt(e.target.value))}
                    />
                    <p className="text-xs text-slate-400">Preavviso per prenotare</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Limite Cancellazione (ore)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={config?.cancellazione_limite || 24}
                      onChange={(e) => updateConfig('cancellazione_limite', parseInt(e.target.value))}
                    />
                    <p className="text-xs text-slate-400">Ore prima per cancellare</p>
                  </div>
                </div>
              </div>

              {/* RESET DATABASE - ZONA PERICOLOSA */}
              <div className="border-t border-red-200 pt-6 mt-6">
                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Reset Database - Preparazione Deploy
                  </h4>
                  <p className="text-sm text-red-600 mb-4">
                    Cancella tutti i dati di test (appuntamenti, clienti, transazioni) per preparare l'app all'uso reale.
                    <strong> Servizi, operatori e configurazione verranno mantenuti.</strong>
                  </p>
                  <Button
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-100"
                    onClick={() => setResetDialogOpen(true)}
                  >
                    <RefreshCcw className="w-4 h-4 mr-2" />
                    Reset Database
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ORARI */}
        <TabsContent value="orari">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-600" />
                Orari di Apertura
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {GIORNI.map(({ key, label }) => {
                  const orario = config?.orari?.[key] || {};
                  return (
                    <div 
                      key={key} 
                      className={`p-4 rounded-xl border ${orario.attivo ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100'}`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={orario.attivo}
                            onCheckedChange={(checked) => updateOrario(key, 'attivo', checked)}
                          />
                          <span className={`font-semibold ${orario.attivo ? 'text-slate-800' : 'text-slate-400'}`}>
                            {label}
                          </span>
                        </div>
                        {!orario.attivo && <span className="text-sm text-red-500 font-medium">CHIUSO</span>}
                      </div>
                      
                      {orario.attivo && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div className="space-y-1">
                            <Label className="text-xs">Apertura</Label>
                            <Input
                              type="time"
                              value={orario.apertura || "09:00"}
                              onChange={(e) => updateOrario(key, 'apertura', e.target.value)}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Chiusura</Label>
                            <Input
                              type="time"
                              value={orario.chiusura || "19:00"}
                              onChange={(e) => updateOrario(key, 'chiusura', e.target.value)}
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Pausa Inizio</Label>
                            <Input
                              type="time"
                              value={orario.pausa_inizio || ""}
                              onChange={(e) => updateOrario(key, 'pausa_inizio', e.target.value || null)}
                              placeholder="--:--"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Pausa Fine</Label>
                            <Input
                              type="time"
                              value={orario.pausa_fine || ""}
                              onChange={(e) => updateOrario(key, 'pausa_fine', e.target.value || null)}
                              placeholder="--:--"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Chiusure Straordinarie del Salone */}
          <Card className="border-0 shadow-lg mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarOff className="w-5 h-5 text-red-600" />
                Chiusure Straordinarie del Salone
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">
                  Aggiungi festività, ferie collettive o chiusure straordinarie valide per tutto il salone
                </p>
                <Button onClick={addChiusuraSalone} variant="outline" size="sm">
                  <Plus className="w-3 h-3 mr-1" /> Aggiungi Chiusura
                </Button>
              </div>

              {(!config?.chiusure_straordinarie || config.chiusure_straordinarie.length === 0) ? (
                <div className="text-center py-8 bg-slate-50 rounded-lg">
                  <CalendarOff className="w-12 h-12 mx-auto text-slate-300 mb-2" />
                  <p className="text-slate-400">Nessuna chiusura straordinaria programmata</p>
                  <p className="text-xs text-slate-400 mt-1">
                    Aggiungi festività come Natale, Pasqua, o periodi di ferie estive
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {config.chiusure_straordinarie.map((chiusura, index) => (
                    <div 
                      key={chiusura.id || index}
                      className="flex items-center gap-3 p-4 bg-red-50 rounded-lg border border-red-200"
                    >
                      <Palmtree className="w-5 h-5 text-red-500 flex-shrink-0" />
                      
                      <select
                        value={chiusura.tipo || "singolo"}
                        onChange={(e) => updateChiusuraSalone(index, "tipo", e.target.value)}
                        className="text-sm border rounded px-2 py-1.5 bg-white"
                      >
                        <option value="singolo">Giorno singolo</option>
                        <option value="range">Periodo</option>
                      </select>
                      
                      <div className="flex items-center gap-2">
                        <Input
                          type="date"
                          value={chiusura.data || ""}
                          onChange={(e) => updateChiusuraSalone(index, "data", e.target.value)}
                          className="w-40"
                        />
                        
                        {chiusura.tipo === "range" && (
                          <>
                            <span className="text-slate-400">→</span>
                            <Input
                              type="date"
                              value={chiusura.data_fine || ""}
                              onChange={(e) => updateChiusuraSalone(index, "data_fine", e.target.value)}
                              className="w-40"
                            />
                          </>
                        )}
                      </div>
                      
                      <Input
                        type="text"
                        value={chiusura.motivo || ""}
                        onChange={(e) => updateChiusuraSalone(index, "motivo", e.target.value)}
                        className="flex-1"
                        placeholder="Motivo (es. Natale, Ferie estive...)"
                      />
                      
                      <Button 
                        variant="ghost"
                        size="sm"
                        onClick={() => removeChiusuraSalone(index)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-100"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Festività predefinite italiane */}
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-2">Festività italiane comuni</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { data: "2026-01-01", motivo: "Capodanno" },
                    { data: "2026-01-06", motivo: "Epifania" },
                    { data: "2026-04-05", motivo: "Pasqua" },
                    { data: "2026-04-06", motivo: "Pasquetta" },
                    { data: "2026-04-25", motivo: "Liberazione" },
                    { data: "2026-05-01", motivo: "Festa Lavoratori" },
                    { data: "2026-06-02", motivo: "Repubblica" },
                    { data: "2026-08-15", motivo: "Ferragosto" },
                    { data: "2026-11-01", motivo: "Ognissanti" },
                    { data: "2026-12-08", motivo: "Immacolata" },
                    { data: "2026-12-25", motivo: "Natale" },
                    { data: "2026-12-26", motivo: "S. Stefano" },
                  ].map(fest => {
                    const isAdded = (config?.chiusure_straordinarie || []).some(c => c.data === fest.data);
                    return (
                      <button
                        key={fest.data}
                        type="button"
                        disabled={isAdded}
                        onClick={() => {
                          setConfig(prev => ({
                            ...prev,
                            chiusure_straordinarie: [...(prev.chiusure_straordinarie || []), {
                              id: Date.now().toString(),
                              data: fest.data,
                              data_fine: "",
                              motivo: fest.motivo,
                              tipo: "singolo"
                            }]
                          }));
                        }}
                        className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                          isAdded 
                            ? 'bg-green-100 text-green-700 cursor-not-allowed' 
                            : 'bg-white text-blue-700 hover:bg-blue-100 border border-blue-200'
                        }`}
                      >
                        {isAdded ? '✓ ' : '+ '}{fest.motivo}
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="colorbar">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="w-5 h-5 text-purple-600" />
                Configurazione Color Bar
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Trattamenti che attivano Color Bar */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-800">Trattamenti che attivano il Color Bar</h3>
                    <p className="text-sm text-slate-500">Servizi per cui viene mostrato il pulsante Color Bar</p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      const nuovo = prompt("Nome trattamento da aggiungere:");
                      if (nuovo && nuovo.trim()) {
                        const lista = config?.colorbar_trattamenti || [];
                        updateConfig('colorbar_trattamenti', [...lista, nuovo.trim().toLowerCase()]);
                      }
                    }}
                    className="gap-1"
                  >
                    <Plus className="w-4 h-4" /> Aggiungi
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(config?.colorbar_trattamenti || ['colore', 'tinta', 'gloss', 'tonalizzante', 'trattamento', 'taglio', 'meches', 'balayage', 'shatush', 'decolorazione', 'schiaritura']).map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm">
                      {t}
                      <button 
                        onClick={() => {
                          const lista = config?.colorbar_trattamenti || [];
                          updateConfig('colorbar_trattamenti', lista.filter(x => x !== t));
                        }}
                        className="ml-1 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Esclusioni */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-800">Servizi Esclusi</h3>
                    <p className="text-sm text-slate-500">Servizi per cui NON viene mostrato il Color Bar</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const nuovo = prompt("Nome servizio da escludere:");
                      if (nuovo && nuovo.trim()) {
                        const lista = config?.colorbar_esclusioni || [];
                        updateConfig('colorbar_esclusioni', [...lista, nuovo.trim().toLowerCase()]);
                      }
                    }}
                    className="gap-1"
                  >
                    <Plus className="w-4 h-4" /> Aggiungi
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(config?.colorbar_esclusioni || ['piega', 'brushing', 'messa in piega']).map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm">
                      {t}
                      <button 
                        onClick={() => {
                          const lista = config?.colorbar_esclusioni || [];
                          updateConfig('colorbar_esclusioni', lista.filter(x => x !== t));
                        }}
                        className="ml-1 hover:text-red-900"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Tipi di Colore */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-800">Tipi di Colore</h3>
                    <p className="text-sm text-slate-500">Opzioni disponibili nel menu "Tipo Colore"</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const nuovo = prompt("Nome tipo colore da aggiungere:");
                      if (nuovo && nuovo.trim()) {
                        const lista = config?.colorbar_tipi_colore || [];
                        updateConfig('colorbar_tipi_colore', [...lista, nuovo.trim()]);
                      }
                    }}
                    className="gap-1"
                  >
                    <Plus className="w-4 h-4" /> Aggiungi
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(config?.colorbar_tipi_colore || ['Permanente', 'Semipermanente', 'Tonalizzante', 'Gloss', 'Riflessante']).map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm">
                      {t}
                      <button 
                        onClick={() => {
                          const lista = config?.colorbar_tipi_colore || [];
                          updateConfig('colorbar_tipi_colore', lista.filter(x => x !== t));
                        }}
                        className="ml-1 hover:text-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Tempi di Posa Predefiniti */}
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-slate-800">Tempi di Posa Predefiniti</h3>
                  <p className="text-sm text-slate-500">Tempo di posa automatico in base al tipo di colore</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {(config?.colorbar_tipi_colore || ['Permanente', 'Semipermanente', 'Tonalizzante', 'Gloss', 'Riflessante']).map((tipo) => (
                    <div key={tipo} className="space-y-1">
                      <Label className="text-sm">{tipo}</Label>
                      <Input
                        placeholder="Es: 35 min"
                        value={config?.colorbar_tempi_posa?.[tipo] || ''}
                        onChange={(e) => {
                          const tempi = { ...(config?.colorbar_tempi_posa || {}), [tipo]: e.target.value };
                          updateConfig('colorbar_tempi_posa', tempi);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Domande Taglio */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-800">Domande Annotazioni Taglio</h3>
                    <p className="text-sm text-slate-500">Domande mostrate per i servizi di taglio</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const nuova = prompt("Nuova domanda da aggiungere:");
                      if (nuova && nuova.trim()) {
                        const lista = config?.colorbar_domande_taglio || [];
                        updateConfig('colorbar_domande_taglio', [...lista, nuova.trim()]);
                      }
                    }}
                    className="gap-1"
                  >
                    <Plus className="w-4 h-4" /> Aggiungi
                  </Button>
                </div>
                <div className="space-y-2">
                  {(config?.colorbar_domande_taglio || ['Preferenza lunghezza?', 'Scalature sì/no?', 'Frangia sì/no?', 'Sfumatura laterale?', 'Prodotto styling preferito?']).map((d) => (
                    <div key={d} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                      <span className="flex-1 text-sm">{d}</span>
                      <button 
                        onClick={() => {
                          const lista = config?.colorbar_domande_taglio || [];
                          updateConfig('colorbar_domande_taglio', lista.filter(x => x !== d));
                        }}
                        className="p-1 hover:bg-red-100 rounded text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* COLORI */}
        <TabsContent value="colori">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Paintbrush className="w-5 h-5 text-blue-600" />
                Personalizza Colori
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label>Colore Primario</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={config?.colore_primario || "#2563eb"}
                      onChange={(e) => updateConfig('colore_primario', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer border-0"
                    />
                    <Input
                      value={config?.colore_primario || "#2563eb"}
                      onChange={(e) => updateConfig('colore_primario', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-slate-400">Pulsanti e accenti principali</p>
                </div>

                <div className="space-y-2">
                  <Label>Colore Secondario</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={config?.colore_secondario || "#0f172a"}
                      onChange={(e) => updateConfig('colore_secondario', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer border-0"
                    />
                    <Input
                      value={config?.colore_secondario || "#0f172a"}
                      onChange={(e) => updateConfig('colore_secondario', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-slate-400">Sidebar e navbar</p>
                </div>

                <div className="space-y-2">
                  <Label>Colore Accento</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={config?.colore_accento || "#10b981"}
                      onChange={(e) => updateConfig('colore_accento', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer border-0"
                    />
                    <Input
                      value={config?.colore_accento || "#10b981"}
                      onChange={(e) => updateConfig('colore_accento', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                  <p className="text-xs text-slate-400">Successo e conferme</p>
                </div>

                <div className="space-y-2">
                  <Label>Colore Servizi Donna</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={config?.colore_donna || "#ec4899"}
                      onChange={(e) => updateConfig('colore_donna', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer border-0"
                    />
                    <Input
                      value={config?.colore_donna || "#ec4899"}
                      onChange={(e) => updateConfig('colore_donna', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Colore Servizi Uomo</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={config?.colore_uomo || "#3b82f6"}
                      onChange={(e) => updateConfig('colore_uomo', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer border-0"
                    />
                    <Input
                      value={config?.colore_uomo || "#3b82f6"}
                      onChange={(e) => updateConfig('colore_uomo', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Colore Servizi Unisex</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={config?.colore_unisex || "#8b5cf6"}
                      onChange={(e) => updateConfig('colore_unisex', e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer border-0"
                    />
                    <Input
                      value={config?.colore_unisex || "#8b5cf6"}
                      onChange={(e) => updateConfig('colore_unisex', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>

              {/* Preview */}
              <div className="mt-8 p-6 rounded-xl border border-slate-200">
                <h4 className="font-semibold text-slate-800 mb-4">Anteprima</h4>
                <div className="flex gap-4 flex-wrap">
                  <button 
                    className="px-4 py-2 rounded-lg text-white font-medium"
                    style={{ backgroundColor: config?.colore_primario }}
                  >
                    Pulsante Primario
                  </button>
                  <div 
                    className="px-4 py-2 rounded-lg text-white font-medium"
                    style={{ backgroundColor: config?.colore_secondario }}
                  >
                    Secondario
                  </div>
                  <div 
                    className="px-4 py-2 rounded-lg text-white font-medium"
                    style={{ backgroundColor: config?.colore_accento }}
                  >
                    Accento
                  </div>
                  <div 
                    className="px-4 py-2 rounded-lg text-white font-medium"
                    style={{ backgroundColor: config?.colore_donna }}
                  >
                    Donna
                  </div>
                  <div 
                    className="px-4 py-2 rounded-lg text-white font-medium"
                    style={{ backgroundColor: config?.colore_uomo }}
                  >
                    Uomo
                  </div>
                  <div 
                    className="px-4 py-2 rounded-lg text-white font-medium"
                    style={{ backgroundColor: config?.colore_unisex }}
                  >
                    Unisex
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PAGAMENTI */}
        <TabsContent value="pagamenti">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-blue-600" />
                Metodi di Pagamento
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-500 mb-6">Seleziona i metodi di pagamento accettati nel salone</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {METODI_PAGAMENTO_OPTIONS.map(metodo => {
                  const isActive = config?.metodi_pagamento?.includes(metodo.value);
                  return (
                    <div
                      key={metodo.value}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        isActive 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => toggleMetodoPagamento(metodo.value)}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-medium ${isActive ? 'text-blue-700' : 'text-slate-600'}`}>
                          {metodo.label}
                        </span>
                        <Switch
                          checked={isActive}
                          onCheckedChange={() => toggleMetodoPagamento(metodo.value)}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* NOTIFICHE */}
        <TabsContent value="notifiche">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-600" />
                Configurazione Notifiche
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-slate-600">
                Configura i servizi di notifica per inviare promemoria ai clienti via WhatsApp e Email.
                I promemoria possono essere inviati manualmente dall'Agenda cliccando su un appuntamento.
              </p>
              
              {/* WhatsApp - Twilio */}
              <div className="p-6 bg-green-50 rounded-lg border border-green-200 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-green-800">WhatsApp (Twilio)</h3>
                    <p className="text-sm text-green-600">Invia promemoria tramite WhatsApp Business</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-green-700">Account SID</Label>
                    <Input 
                      type="password"
                      placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-green-700">Auth Token</Label>
                    <Input 
                      type="password"
                      placeholder="Token segreto"
                      className="mt-1"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-green-700">Numero WhatsApp</Label>
                    <Input 
                      type="text"
                      placeholder="+14155238886 (Sandbox) o il tuo numero registrato"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-2 text-sm text-green-700">
                  <AlertCircle className="w-4 h-4" />
                  <span>Le credenziali vanno configurate nel file .env del backend per sicurezza</span>
                </div>
              </div>
              
              {/* Email - SendGrid */}
              <div className="p-6 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-800">Email (SendGrid)</h3>
                    <p className="text-sm text-blue-600">Invia promemoria con allegato calendario (.ics)</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-blue-700">API Key</Label>
                    <Input 
                      type="password"
                      placeholder="SG.xxxxxxxxxxxxxxxxxxxxxxxx"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-blue-700">Email Mittente</Label>
                    <Input 
                      type="email"
                      placeholder="noreply@tuosalone.it"
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <div className="flex items-center gap-2 text-sm text-blue-700">
                  <AlertCircle className="w-4 h-4" />
                  <span>Le credenziali vanno configurate nel file .env del backend per sicurezza</span>
                </div>
              </div>
              
              {/* Contenuto promemoria */}
              <div className="p-6 bg-slate-50 rounded-lg border border-slate-200 space-y-4">
                <h3 className="font-semibold text-slate-800">Contenuto del Promemoria</h3>
                <p className="text-sm text-slate-600">
                  I promemoria includono automaticamente:
                </p>
                <ul className="text-sm text-slate-600 list-disc list-inside space-y-1">
                  <li>Data e ora dell'appuntamento</li>
                  <li>Servizi prenotati</li>
                  <li>Indirizzo del salone con link Google Maps</li>
                  <li>File calendario (.ics) per salvare l'appuntamento</li>
                </ul>
              </div>
              
              {/* Istruzioni */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <h4 className="font-medium text-amber-800 mb-2">Come configurare</h4>
                <ol className="text-sm text-amber-700 list-decimal list-inside space-y-1">
                  <li>Crea un account su <a href="https://www.twilio.com" target="_blank" rel="noopener noreferrer" className="underline">Twilio</a> per WhatsApp</li>
                  <li>Crea un account su <a href="https://sendgrid.com" target="_blank" rel="noopener noreferrer" className="underline">SendGrid</a> per Email</li>
                  <li>Ottieni le API Key dai rispettivi dashboard</li>
                  <li>Aggiungi le credenziali al file <code className="bg-amber-100 px-1 rounded">/app/backend/.env</code></li>
                  <li>Riavvia il backend per applicare le modifiche</li>
                </ol>
              </div>

              {/* Google Sheets Integration */}
              <div className="p-6 bg-emerald-50 rounded-lg border border-emerald-200 space-y-4 mt-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-lg">
                    <svg className="w-6 h-6 text-emerald-600" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.5 3.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5zM19 19.09H5V4.91h14v14.18zM6 15h12v2H6zm0-4h12v2H6zm0-4h12v2H6z"/>
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-emerald-800">Google Sheets - Consulenze</h3>
                    <p className="text-sm text-emerald-600">Sincronizza le schede consulenza con un foglio Google</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-emerald-700">ID Foglio Google Sheets</Label>
                    <Input 
                      type="text"
                      placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
                      className="mt-1"
                      defaultValue={config?.google_sheets_id || ""}
                      onChange={(e) => setConfig(prev => ({ ...prev, google_sheets_id: e.target.value }))}
                    />
                    <p className="text-xs text-emerald-600 mt-1">
                      Trova l'ID nell'URL del foglio: docs.google.com/spreadsheets/d/<strong>[ID]</strong>/edit
                    </p>
                  </div>
                </div>
                
                <div className="p-4 bg-emerald-100 rounded-lg">
                  <h4 className="font-medium text-emerald-800 mb-2">Come configurare Google Sheets</h4>
                  <ol className="text-sm text-emerald-700 list-decimal list-inside space-y-1">
                    <li>Vai su <a href="https://docs.google.com/spreadsheets" target="_blank" rel="noopener noreferrer" className="underline">Google Sheets</a> e crea un nuovo foglio</li>
                    <li>Aggiungi le intestazioni nella prima riga: <code className="bg-emerald-200 px-1 rounded text-xs">Data, Cliente, Tipo, Obiettivo, Stato Capelli, Problematiche, Note</code></li>
                    <li>Copia l'ID del foglio dall'URL</li>
                    <li>Condividi il foglio con l'email del service account (se usi autenticazione avanzata)</li>
                    <li>Oppure imposta "Chiunque con il link può modificare"</li>
                  </ol>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* MARKETING & ANALISI */}
        <TabsContent value="marketing">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                Impostazioni Marketing & Analisi
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Obiettivi */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500" />
                  Obiettivi
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Obiettivo Mensile (€)</Label>
                    <Input
                      type="number"
                      value={config?.obiettivo_mensile || ""}
                      onChange={(e) => updateConfig('obiettivo_mensile', parseFloat(e.target.value) || 0)}
                      placeholder="10000"
                    />
                    <p className="text-xs text-slate-500">Target di incasso mensile</p>
                  </div>
                  <div className="space-y-2">
                    <Label>PIN Obiettivo (4 cifre)</Label>
                    <Input
                      type="password"
                      maxLength={4}
                      value={config?.obiettivo_pin || ""}
                      onChange={(e) => updateConfig('obiettivo_pin', e.target.value)}
                      placeholder="****"
                    />
                    <p className="text-xs text-slate-500">PIN per modificare l'obiettivo</p>
                  </div>
                </div>
              </div>

              {/* Recall Marketing */}
              <div className="space-y-4 border-t pt-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <Mail className="w-5 h-5 text-blue-500" />
                  Recall Marketing
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Giorni Recall Donna</Label>
                    <Input
                      type="number"
                      value={config?.recall_giorni_donna || 45}
                      onChange={(e) => updateConfig('recall_giorni_donna', parseInt(e.target.value) || 45)}
                      placeholder="45"
                    />
                    <p className="text-xs text-slate-500">Giorni dopo l'ultimo appuntamento</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Giorni Recall Uomo</Label>
                    <Input
                      type="number"
                      value={config?.recall_giorni_uomo || 30}
                      onChange={(e) => updateConfig('recall_giorni_uomo', parseInt(e.target.value) || 30)}
                      placeholder="30"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Giorni Compleanno</Label>
                    <Input
                      type="number"
                      value={config?.recall_giorni_compleanno || 7}
                      onChange={(e) => updateConfig('recall_giorni_compleanno', parseInt(e.target.value) || 7)}
                      placeholder="7"
                    />
                    <p className="text-xs text-slate-500">Giorni prima del compleanno</p>
                  </div>
                </div>
              </div>

              {/* Template Messaggi */}
              <div className="space-y-4 border-t pt-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-green-500" />
                  Template Messaggi
                </h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Messaggio Recall</Label>
                    <Textarea
                      value={config?.template_recall || "Ciao {nome}, sono passati {giorni} giorni dal tuo ultimo appuntamento! Ti aspettiamo per il tuo prossimo trattamento. Prenota ora!"}
                      onChange={(e) => updateConfig('template_recall', e.target.value)}
                      rows={3}
                      placeholder="Usa {nome}, {giorni} come variabili"
                    />
                    <p className="text-xs text-slate-500">Variabili: {'{nome}'}, {'{giorni}'}, {'{salone}'}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Messaggio Compleanno</Label>
                    <Textarea
                      value={config?.template_compleanno || "Buon compleanno {nome}! 🎂 Per festeggiare ti regaliamo uno sconto del 10% sul prossimo trattamento. Prenota ora!"}
                      onChange={(e) => updateConfig('template_compleanno', e.target.value)}
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Messaggio Promemoria Appuntamento</Label>
                    <Textarea
                      value={config?.template_promemoria || "Ciao {nome}, ti ricordiamo l'appuntamento di domani alle {ora} presso {salone}. A presto!"}
                      onChange={(e) => updateConfig('template_promemoria', e.target.value)}
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Analisi */}
              <div className="space-y-4 border-t pt-4">
                <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-orange-500" />
                  Impostazioni Analisi
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Periodo Default Report (giorni)</Label>
                    <Select 
                      value={String(config?.analisi_periodo_default || 30)} 
                      onValueChange={(v) => updateConfig('analisi_periodo_default', parseInt(v))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="7">Ultima settimana</SelectItem>
                        <SelectItem value="30">Ultimo mese</SelectItem>
                        <SelectItem value="90">Ultimi 3 mesi</SelectItem>
                        <SelectItem value="365">Ultimo anno</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Mostra Confronto Anno Precedente</Label>
                    <div className="flex items-center gap-2 pt-2">
                      <Switch
                        checked={config?.analisi_confronto_anno || false}
                        onCheckedChange={(v) => updateConfig('analisi_confronto_anno', v)}
                      />
                      <span className="text-sm text-slate-600">
                        {config?.analisi_confronto_anno ? 'Attivo' : 'Disattivo'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pulsante Salva */}
              <div className="flex justify-end pt-4 border-t">
                <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                  <Save className="w-4 h-4 mr-2" />
                  Salva Impostazioni Marketing
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB MAGAZZINO - Soglia sottoscorta + PIN Card Attive */}
        <TabsContent value="magazzino">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-amber-600" />
                Pannello Controllo Magazzino
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="soglia-globale" className="text-sm font-semibold">
                    Soglia Sottoscorta Globale
                  </Label>
                  <p className="text-xs text-slate-500">
                    Valore predefinito usato quando crei un nuovo prodotto. I prodotti con scorta inferiore a questa soglia verranno mostrati come "sotto scorta" nella Dashboard e nel Magazzino.
                  </p>
                  <Input
                    id="soglia-globale"
                    type="number"
                    min="0"
                    value={config.soglia_sottoscorta_globale ?? 5}
                    onChange={(e) => setConfig({ ...config, soglia_sottoscorta_globale: parseInt(e.target.value) || 0 })}
                    placeholder="5"
                    data-testid="input-soglia-sottoscorta"
                    className="max-w-xs"
                  />
                  <p className="text-[11px] text-slate-400">Suggerito: 3–10 unità a seconda del consumo</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pin-card-attive" className="text-sm font-semibold">
                    PIN Riepilogo Card Attive
                  </Label>
                  <p className="text-xs text-slate-500">
                    Imposta un PIN numerico (4–6 cifre) per proteggere la visualizzazione del riepilogo Card Attive nella Dashboard. Lasciato vuoto = accesso libero.
                  </p>
                  <Input
                    id="pin-card-attive"
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    value={config.pin_card_attive ?? ""}
                    onChange={(e) => setConfig({ ...config, pin_card_attive: e.target.value.replace(/\D/g, "") })}
                    placeholder="Es. 1234"
                    data-testid="input-pin-card-attive"
                    className="max-w-xs"
                  />
                  <p className="text-[11px] text-slate-400">Lascialo vuoto se non vuoi proteggere la visualizzazione</p>
                </div>
              </div>

              <div className="border-t pt-4">
                <Button
                  onClick={handleSave}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="salva-impostazioni-magazzino"
                >
                  Salva Impostazioni
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>


        {/* TAB SICUREZZA - Cambio Password */}
        <TabsContent value="sicurezza">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-blue-600" />
                Cambia Password
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="max-w-md space-y-4">
                <div className="space-y-2">
                  <Label>Password Attuale</Label>
                  <Input
                    type="password"
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    placeholder="Inserisci la password attuale"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nuova Password</Label>
                  <Input
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                    placeholder="Minimo 6 caratteri"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Conferma Nuova Password</Label>
                  <Input
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                    placeholder="Ripeti la nuova password"
                  />
                </div>
                <Button 
                  onClick={handleChangePassword} 
                  disabled={changingPassword}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {changingPassword ? "Cambio in corso..." : "Cambia Password"}
                </Button>
              </div>

              <div className="border-t pt-6 mt-6">
                <h3 className="font-semibold text-slate-700 mb-4">Recupero Password via Email</h3>
                <p className="text-sm text-slate-500 mb-4">
                  Se dimentichi la password, puoi richiedere un link di reset via email dalla pagina di login.
                </p>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="w-5 h-5" />
                    <span className="font-medium">Email configurata: icirillo2021@gmail.com</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB UTENTI - Gestione Accessi */}
        <TabsContent value="utenti">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                  Gestione Utenti
                </div>
                <Button onClick={() => setUserDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700 gap-2">
                  <Plus className="w-4 h-4" />
                  Nuovo Utente
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadingUsers ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun utente trovato</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {users.map((user) => (
                    <div 
                      key={user.id || user.email} 
                      className={`p-4 rounded-lg border ${user.attivo !== false ? 'bg-white' : 'bg-slate-50 opacity-60'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                            user.ruolo === 'admin' ? 'bg-purple-600' : 'bg-blue-600'
                          }`}>
                            {user.nome?.charAt(0)?.toUpperCase() || user.email?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold">{user.nome || user.email}</p>
                            <p className="text-sm text-slate-500">{user.email}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              user.ruolo === 'admin' 
                                ? 'bg-purple-100 text-purple-700' 
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {user.ruolo === 'admin' ? 'Amministratore' : 'Operatore'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleUser(user)}
                            className={user.attivo !== false ? 'text-orange-600' : 'text-green-600'}
                          >
                            {user.attivo !== false ? 'Disattiva' : 'Attiva'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* INTEGRAZIONI */}
        <TabsContent value="integrazioni">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Link2 className="w-5 h-5 text-green-600" />
                Integrazioni WhatsApp
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Stato Connessione */}
              <div className={`p-4 rounded-xl border-2 ${whatsappConfig.connected ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${whatsappConfig.connected ? 'bg-green-500' : 'bg-slate-300'}`}>
                      <Smartphone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-lg">WhatsApp Business</p>
                      {whatsappConfig.connected ? (
                        <p className="text-sm text-green-600 flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4" /> Connesso: {whatsappConfig.phoneNumber}
                        </p>
                      ) : (
                        <p className="text-sm text-slate-500">Non connesso</p>
                      )}
                    </div>
                  </div>
                  <Button 
                    variant={whatsappConfig.connected ? "outline" : "default"}
                    className={whatsappConfig.connected ? "border-red-200 text-red-600 hover:bg-red-50" : "bg-green-600 hover:bg-green-700"}
                    onClick={() => {
                      if (whatsappConfig.connected) {
                        setWhatsappConfig(prev => ({ ...prev, connected: false, phoneNumber: "" }));
                        toast.info("WhatsApp disconnesso");
                      } else {
                        setQrStatus('waiting');
                        setQrDialogOpen(true);
                      }
                    }}
                    data-testid="whatsapp-qr-btn"
                  >
                    <QrCode className="w-4 h-4 mr-2" />
                    {whatsappConfig.connected ? "Disconnetti" : "Connetti con QR"}
                  </Button>
                </div>
              </div>

              {/* Destinatari */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold flex items-center gap-2">
                    <Phone className="w-4 h-4 text-blue-600" />
                    Numeri Destinatari
                  </Label>
                </div>
                <p className="text-sm text-slate-500">
                  Aggiungi i numeri di telefono che riceveranno le notifiche e i report.
                </p>
                
                {/* Lista destinatari */}
                <div className="space-y-2">
                  {whatsappConfig.destinatari.map((dest) => (
                    <div 
                      key={dest.id} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <Phone className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium">{dest.nome}</p>
                          <p className="text-sm text-slate-500">{dest.telefono}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => removeDestinatario(dest.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  
                  {whatsappConfig.destinatari.length === 0 && (
                    <p className="text-center text-slate-400 py-4">Nessun destinatario configurato</p>
                  )}
                </div>

                {/* Aggiungi destinatario */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Nome"
                    value={newDestinatario.nome}
                    onChange={(e) => setNewDestinatario(prev => ({ ...prev, nome: e.target.value }))}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Telefono (es: 3331234567)"
                    value={newDestinatario.telefono}
                    onChange={(e) => setNewDestinatario(prev => ({ ...prev, telefono: e.target.value }))}
                    className="flex-1"
                  />
                  <Button onClick={addDestinatario} className="bg-green-600 hover:bg-green-700">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Tipi di Notifiche */}
              <div className="space-y-4">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Bell className="w-4 h-4 text-amber-600" />
                  Notifiche da Inviare
                </Label>
                <p className="text-sm text-slate-500">
                  Seleziona quali informazioni vuoi ricevere via WhatsApp.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Promemoria Appuntamenti */}
                  <div 
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      whatsappConfig.notifiche.promemoria_appuntamenti 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => toggleNotifica('promemoria_appuntamenti')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-medium">Promemoria Appuntamenti</p>
                          <p className="text-xs text-slate-500">24h prima dell'appuntamento</p>
                        </div>
                      </div>
                      <Switch checked={whatsappConfig.notifiche.promemoria_appuntamenti} />
                    </div>
                  </div>

                  {/* Conferma Prenotazione */}
                  <div 
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      whatsappConfig.notifiche.conferma_prenotazione 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => toggleNotifica('conferma_prenotazione')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-blue-600" />
                        <div>
                          <p className="font-medium">Conferma Prenotazione</p>
                          <p className="text-xs text-slate-500">Quando un cliente prenota</p>
                        </div>
                      </div>
                      <Switch checked={whatsappConfig.notifiche.conferma_prenotazione} />
                    </div>
                  </div>

                  {/* Report Giornaliero */}
                  <div 
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      whatsappConfig.notifiche.report_giornaliero 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => toggleNotifica('report_giornaliero')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-purple-600" />
                        <div>
                          <p className="font-medium">Report Giornaliero</p>
                          <p className="text-xs text-slate-500">Riepilogo fine giornata</p>
                        </div>
                      </div>
                      <Switch checked={whatsappConfig.notifiche.report_giornaliero} />
                    </div>
                  </div>

                  {/* Report Settimanale */}
                  <div 
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      whatsappConfig.notifiche.report_settimanale 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => toggleNotifica('report_settimanale')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-indigo-600" />
                        <div>
                          <p className="font-medium">Report Settimanale</p>
                          <p className="text-xs text-slate-500">Statistiche della settimana</p>
                        </div>
                      </div>
                      <Switch checked={whatsappConfig.notifiche.report_settimanale} />
                    </div>
                  </div>

                  {/* Nuovi Clienti */}
                  <div 
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      whatsappConfig.notifiche.nuovi_clienti 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => toggleNotifica('nuovi_clienti')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <UserPlus className="w-5 h-5 text-amber-600" />
                        <div>
                          <p className="font-medium">Nuovi Clienti</p>
                          <p className="text-xs text-slate-500">Notifica nuovo cliente</p>
                        </div>
                      </div>
                      <Switch checked={whatsappConfig.notifiche.nuovi_clienti} />
                    </div>
                  </div>

                  {/* Cancellazioni */}
                  <div 
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      whatsappConfig.notifiche.cancellazioni 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => toggleNotifica('cancellazioni')}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600" />
                        <div>
                          <p className="font-medium">Cancellazioni</p>
                          <p className="text-xs text-slate-500">Quando un appuntamento viene cancellato</p>
                        </div>
                      </div>
                      <Switch checked={whatsappConfig.notifiche.cancellazioni} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Pulsante Salva */}
              <div className="flex justify-end pt-4 border-t">
                <Button 
                  onClick={saveWhatsappConfig}
                  disabled={savingWhatsapp}
                  className="bg-green-600 hover:bg-green-700 gap-2"
                >
                  {savingWhatsapp ? (
                    <RefreshCcw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Salva Configurazione
                </Button>
              </div>

              {/* Info */}
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="text-sm text-green-700 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>
                    <strong>Pronto!</strong> Clicca "Connetti con QR" per collegare il tuo WhatsApp Business 
                    e iniziare a inviare notifiche ai clienti.
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* QR Code Dialog */}
          <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-green-600" />
                  Connetti WhatsApp Business
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-6 py-4">
                {qrStatus === 'waiting' && (
                  <>
                    {/* QR Code Placeholder */}
                    <div className="flex flex-col items-center">
                      <div className="w-64 h-64 bg-white border-4 border-green-500 rounded-xl p-4 flex items-center justify-center relative">
                        {/* QR Code simulato con pattern */}
                        <div className="w-full h-full bg-gradient-to-br from-slate-900 to-slate-700 rounded-lg relative overflow-hidden">
                          <div className="absolute inset-4 grid grid-cols-8 grid-rows-8 gap-1">
                            {[...Array(64)].map((_, i) => (
                              <div 
                                key={i} 
                                className={`${Math.random() > 0.5 ? 'bg-white' : 'bg-transparent'} rounded-sm`}
                              />
                            ))}
                          </div>
                          {/* Corner markers */}
                          <div className="absolute top-2 left-2 w-10 h-10 border-4 border-white rounded-sm">
                            <div className="absolute inset-2 bg-white rounded-sm" />
                          </div>
                          <div className="absolute top-2 right-2 w-10 h-10 border-4 border-white rounded-sm">
                            <div className="absolute inset-2 bg-white rounded-sm" />
                          </div>
                          <div className="absolute bottom-2 left-2 w-10 h-10 border-4 border-white rounded-sm">
                            <div className="absolute inset-2 bg-white rounded-sm" />
                          </div>
                        </div>
                      </div>
                      <p className="text-center text-sm text-slate-600 mt-4">
                        Scansiona con <strong>WhatsApp Business</strong>
                      </p>
                      <p className="text-center text-xs text-slate-400 mt-1">
                        Apri WhatsApp → Menu → Dispositivi collegati → Collega dispositivo
                      </p>
                    </div>
                    
                    {/* Status */}
                    <div className="flex items-center justify-center gap-2 text-amber-600">
                      <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                      <span className="text-sm">In attesa di scansione...</span>
                    </div>

                    {/* Simula connessione button (per demo) */}
                    <Button 
                      className="w-full bg-green-600 hover:bg-green-700"
                      onClick={() => {
                        setQrStatus('scanning');
                        setTimeout(() => {
                          setQrStatus('connected');
                          setTimeout(() => {
                            setWhatsappConfig(prev => ({ 
                              ...prev, 
                              connected: true, 
                              phoneNumber: "+39 XXX XXX XXXX" 
                            }));
                            setQrDialogOpen(false);
                            toast.success("WhatsApp connesso con successo!");
                          }, 1500);
                        }, 2000);
                      }}
                      data-testid="whatsapp-simulate-scan"
                    >
                      <Smartphone className="w-4 h-4 mr-2" />
                      Simula Scansione (Demo)
                    </Button>
                  </>
                )}

                {qrStatus === 'scanning' && (
                  <div className="flex flex-col items-center py-8">
                    <div className="w-16 h-16 border-4 border-green-500 border-t-transparent rounded-full animate-spin mb-4" />
                    <p className="text-lg font-medium text-slate-700">Connessione in corso...</p>
                    <p className="text-sm text-slate-500">Verifica sul tuo telefono</p>
                  </div>
                )}

                {qrStatus === 'connected' && (
                  <div className="flex flex-col items-center py-8">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <CheckCircle2 className="w-12 h-12 text-green-600" />
                    </div>
                    <p className="text-lg font-medium text-green-700">Connesso!</p>
                    <p className="text-sm text-slate-500">WhatsApp Business collegato con successo</p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* WINGMAN - REPORT TEAM */}
          <Card className="border-0 shadow-lg mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                Wingman - Report Team
              </CardTitle>
              <p className="text-sm text-slate-500">
                Configura i report automatici per te e il tuo team via WhatsApp
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Attivazione */}
              <div className={`p-4 rounded-xl border-2 ${wingmanConfig.attivo ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${wingmanConfig.attivo ? 'bg-amber-500' : 'bg-slate-300'}`}>
                      <Zap className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-lg">Report Automatici</p>
                      <p className="text-sm text-slate-500">
                        {wingmanConfig.attivo ? 'Attivo - Report inviati al team' : 'Disattivato'}
                      </p>
                    </div>
                  </div>
                  <Switch 
                    checked={wingmanConfig.attivo}
                    onCheckedChange={(checked) => setWingmanConfig(prev => ({ ...prev, attivo: checked }))}
                  />
                </div>
              </div>

              {/* Destinatari Team */}
              <div className="space-y-4">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-600" />
                  Team Destinatari Report
                </Label>
                <p className="text-sm text-slate-500">
                  Aggiungi i membri del team che riceveranno i report giornalieri e gli alert.
                </p>
                
                {/* Lista membri */}
                <div className="space-y-2">
                  {wingmanConfig.destinatari_team?.map((member) => (
                    <div 
                      key={member.id} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <Users className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium">{member.nome}</p>
                          <p className="text-sm text-slate-500">{member.telefono} • {member.ruolo}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => removeTeamMember(member.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  
                  {(!wingmanConfig.destinatari_team || wingmanConfig.destinatari_team.length === 0) && (
                    <p className="text-center text-slate-400 py-4">Nessun membro del team configurato</p>
                  )}
                </div>

                {/* Aggiungi membro */}
                <div className="flex gap-2 flex-wrap">
                  <Input
                    placeholder="Nome"
                    value={newTeamMember.nome}
                    onChange={(e) => setNewTeamMember(prev => ({ ...prev, nome: e.target.value }))}
                    className="flex-1 min-w-[120px]"
                  />
                  <Input
                    placeholder="Telefono"
                    value={newTeamMember.telefono}
                    onChange={(e) => setNewTeamMember(prev => ({ ...prev, telefono: e.target.value }))}
                    className="flex-1 min-w-[120px]"
                  />
                  <Select
                    value={newTeamMember.ruolo}
                    onValueChange={(v) => setNewTeamMember(prev => ({ ...prev, ruolo: v }))}
                  >
                    <SelectTrigger className="w-[130px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="titolare">Titolare</SelectItem>
                      <SelectItem value="operatore">Operatore</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={addTeamMember} className="bg-amber-500 hover:bg-amber-600">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Obiettivi */}
              <div className="space-y-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Target className="w-4 h-4 text-green-600" />
                  Obiettivi da Tracciare
                </Label>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" /> Incasso Giornaliero (€)
                    </Label>
                    <Input
                      type="number"
                      value={wingmanConfig.obiettivo_incasso_giornaliero}
                      onChange={(e) => setWingmanConfig(prev => ({ 
                        ...prev, 
                        obiettivo_incasso_giornaliero: parseFloat(e.target.value) || 0 
                      }))}
                      className="bg-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> Appuntamenti/Giorno
                    </Label>
                    <Input
                      type="number"
                      value={wingmanConfig.obiettivo_appuntamenti_giornalieri}
                      onChange={(e) => setWingmanConfig(prev => ({ 
                        ...prev, 
                        obiettivo_appuntamenti_giornalieri: parseInt(e.target.value) || 0 
                      }))}
                      className="bg-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-1">
                      <UserPlus className="w-3 h-3" /> Clienti Nuovi/Settimana
                    </Label>
                    <Input
                      type="number"
                      value={wingmanConfig.obiettivo_clienti_nuovi_settimana}
                      onChange={(e) => setWingmanConfig(prev => ({ 
                        ...prev, 
                        obiettivo_clienti_nuovi_settimana: parseInt(e.target.value) || 0 
                      }))}
                      className="bg-white"
                    />
                  </div>
                </div>
              </div>

              {/* Orari Report */}
              <div className="space-y-4">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4 text-purple-600" />
                  Orari Invio Report
                </Label>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Report Mattina */}
                  <div className={`p-4 rounded-lg border-2 ${wingmanConfig.report_mattina_attivo ? 'border-purple-300 bg-purple-50' : 'border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">🌅</span>
                        <span className="font-medium">Report Mattina</span>
                      </div>
                      <Switch
                        checked={wingmanConfig.report_mattina_attivo}
                        onCheckedChange={(checked) => setWingmanConfig(prev => ({ ...prev, report_mattina_attivo: checked }))}
                      />
                    </div>
                    {wingmanConfig.report_mattina_attivo && (
                      <Input
                        type="time"
                        value={wingmanConfig.report_mattina_ora}
                        onChange={(e) => setWingmanConfig(prev => ({ ...prev, report_mattina_ora: e.target.value }))}
                        className="bg-white"
                      />
                    )}
                    <p className="text-xs text-slate-500 mt-2">Riepilogo giorno prima + obiettivi oggi</p>
                  </div>

                  {/* Report Sera */}
                  <div className={`p-4 rounded-lg border-2 ${wingmanConfig.report_sera_attivo ? 'border-purple-300 bg-purple-50' : 'border-slate-200'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">🌙</span>
                        <span className="font-medium">Report Sera</span>
                      </div>
                      <Switch
                        checked={wingmanConfig.report_sera_attivo}
                        onCheckedChange={(checked) => setWingmanConfig(prev => ({ ...prev, report_sera_attivo: checked }))}
                      />
                    </div>
                    {wingmanConfig.report_sera_attivo && (
                      <Input
                        type="time"
                        value={wingmanConfig.report_sera_ora}
                        onChange={(e) => setWingmanConfig(prev => ({ ...prev, report_sera_ora: e.target.value }))}
                        className="bg-white"
                      />
                    )}
                    <p className="text-xs text-slate-500 mt-2">Riepilogo giornata completata</p>
                  </div>
                </div>
              </div>

              {/* Alert Scorte */}
              <div className={`p-4 rounded-lg border-2 ${wingmanConfig.alert_scorte_attivo ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-amber-600" />
                    <span className="font-medium">Alert Scorte Magazzino</span>
                  </div>
                  <Switch
                    checked={wingmanConfig.alert_scorte_attivo}
                    onCheckedChange={(checked) => setWingmanConfig(prev => ({ ...prev, alert_scorte_attivo: checked }))}
                  />
                </div>
                {wingmanConfig.alert_scorte_attivo && (
                  <div className="flex items-center gap-3">
                    <Label className="text-sm whitespace-nowrap">Soglia minima:</Label>
                    <Input
                      type="number"
                      value={wingmanConfig.soglia_scorte_minima}
                      onChange={(e) => setWingmanConfig(prev => ({ ...prev, soglia_scorte_minima: parseInt(e.target.value) || 0 }))}
                      className="w-24 bg-white"
                    />
                    <span className="text-sm text-slate-500">pezzi</span>
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-2">Ricevi un alert quando un prodotto scende sotto la soglia</p>
              </div>

              {/* Report Mensile */}
              <div className={`p-4 rounded-lg border-2 ${wingmanConfig.report_mensile_attivo ? 'border-blue-300 bg-blue-50' : 'border-slate-200'}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                    <span className="font-medium">Report Mensile</span>
                  </div>
                  <Switch
                    checked={wingmanConfig.report_mensile_attivo}
                    onCheckedChange={(checked) => setWingmanConfig(prev => ({ ...prev, report_mensile_attivo: checked }))}
                  />
                </div>
                {wingmanConfig.report_mensile_attivo && (
                  <div className="flex items-center gap-3">
                    <Label className="text-sm whitespace-nowrap">Giorno del mese:</Label>
                    <Select
                      value={String(wingmanConfig.report_mensile_giorno)}
                      onValueChange={(v) => setWingmanConfig(prev => ({ ...prev, report_mensile_giorno: parseInt(v) }))}
                    >
                      <SelectTrigger className="w-24 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 5, 10, 15, 20, 25, 28].map(g => (
                          <SelectItem key={g} value={String(g)}>{g}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-2">Riepilogo completo del mese con confronti e statistiche</p>
              </div>

              {/* Anteprima Report */}
              <div className="space-y-3">
                <Label className="text-base font-semibold flex items-center gap-2">
                  <Eye className="w-4 h-4 text-slate-600" />
                  Anteprima Report
                </Label>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadReportPreview('giornaliero')}
                    disabled={loadingReport}
                  >
                    {loadingReport ? <RefreshCcw className="w-4 h-4 animate-spin mr-1" /> : null}
                    📊 Report Giornaliero
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadReportPreview('mensile')}
                    disabled={loadingReport}
                  >
                    📈 Report Mensile
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadReportPreview('scorte')}
                    disabled={loadingReport}
                  >
                    📦 Report Scorte
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => loadReportPreview('obiettivi')}
                    disabled={loadingReport}
                  >
                    🎯 Stato Obiettivi
                  </Button>
                </div>

                {/* Preview del messaggio */}
                {reportPreview && (
                  <div className="mt-4 p-4 bg-slate-800 text-green-400 rounded-xl font-mono text-sm whitespace-pre-wrap max-h-80 overflow-y-auto">
                    <div className="flex items-center gap-2 mb-2 text-slate-400">
                      <MessageCircle className="w-4 h-4" />
                      <span>Anteprima messaggio WhatsApp:</span>
                    </div>
                    {reportPreview.data?.messaggio_whatsapp || "Nessun messaggio disponibile"}
                  </div>
                )}
              </div>

              {/* Pulsante Salva */}
              <div className="flex justify-end pt-4 border-t">
                <Button 
                  onClick={saveWingmanConfig}
                  disabled={savingWingman}
                  className="bg-amber-500 hover:bg-amber-600 gap-2"
                >
                  {savingWingman ? (
                    <RefreshCcw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  Salva Configurazione Report
                </Button>
              </div>

              {/* Info Wingman */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-700 flex items-start gap-2">
                  <Zap className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Wingman:</strong> Configura i tuoi obiettivi e i membri del team. 
                    I report verranno inviati automaticamente via WhatsApp agli orari impostati.
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Nuovo Utente */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-blue-600" />
              Nuovo Utente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                value={newUserData.nome}
                onChange={(e) => setNewUserData({ ...newUserData, nome: e.target.value })}
                placeholder="Es: Mario Rossi"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUserData.email}
                onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                placeholder="email@esempio.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                value={newUserData.password}
                onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                placeholder="Minimo 6 caratteri"
              />
            </div>
            <div className="space-y-2">
              <Label>Ruolo</Label>
              <Select
                value={newUserData.ruolo}
                onValueChange={(v) => setNewUserData({ ...newUserData, ruolo: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="operatore">Operatore</SelectItem>
                  <SelectItem value="admin">Amministratore</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={handleCreateUser} className="w-full bg-blue-600 hover:bg-blue-700">
              Crea Utente
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Reset Database */}
      <Dialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-6 h-6" />
              Reset Database
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Questa azione cancellerà permanentemente:
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-red-50 p-4 rounded-lg border border-red-200">
              <ul className="text-sm text-red-700 space-y-1">
                <li>• Tutti gli appuntamenti</li>
                <li>• Tutte le transazioni cassa</li>
                <li>• Tutti i preventivi</li>
                <li>• Tutte le card prepagate</li>
                <li>• Tutte le consulenze</li>
                <li>• Statistiche e storico</li>
              </ul>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <p className="text-sm font-medium text-green-700 mb-1">Verranno mantenuti:</p>
              <ul className="text-sm text-green-600 space-y-1">
                <li>✓ <strong>CLIENTI (anagrafica)</strong></li>
                <li>✓ Servizi e categorie</li>
                <li>✓ Operatori</li>
                <li>✓ Configurazione (orari, logo, colori)</li>
                <li>✓ Prodotti magazzino</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Digita <span className="font-bold text-red-600">RESET</span> per confermare:
              </Label>
              <Input
                value={resetConfirm}
                onChange={(e) => setResetConfirm(e.target.value.toUpperCase())}
                placeholder="Digita RESET"
                className="font-mono"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setResetDialogOpen(false);
                setResetConfirm("");
              }}
            >
              Annulla
            </Button>
            <Button
              variant="destructive"
              onClick={handleResetDatabase}
              disabled={resetConfirm !== "RESET" || resetting}
              className="bg-red-600 hover:bg-red-700"
            >
              {resetting ? (
                <>
                  <RefreshCcw className="w-4 h-4 mr-2 animate-spin" />
                  Reset in corso...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Conferma Reset
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
